import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Instagram, Mail, MessageCircle } from 'lucide-react';
import { useLang } from '@/contexts/LanguageContext';
import { BRAND, WHATSAPP_LINK } from '@/data/brand';
import { CONTACT } from '@/constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.09a8.16 8.16 0 0 0 4.77 1.52V6.16a4.85 4.85 0 0 1-1.84-.47z"/>
  </svg>
);

export default function Contact() {
  const { t } = useLang();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t('contact.success'));
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast.error(t('contact.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const channels = [
    {
      key: 'whatsapp',
      icon: MessageCircle,
      value: BRAND.whatsappDisplay,
      href: WHATSAPP_LINK('Hola Jelly! 💕'),
      testId: CONTACT.whatsapp,
      bg: '#25D366',
    },
    {
      key: 'instagram',
      icon: Instagram,
      value: `@${BRAND.instagram}`,
      href: `https://instagram.com/${BRAND.instagram}`,
      testId: CONTACT.instagram,
      bg: '#E1306C',
    },
    {
      key: 'tiktok',
      icon: TikTokIcon,
      value: `@${BRAND.tiktok}`,
      href: `https://tiktok.com/@${BRAND.tiktok}`,
      testId: CONTACT.tiktok,
      bg: '#1F1A24',
    },
    {
      key: 'email',
      icon: Mail,
      value: BRAND.email,
      href: `mailto:${BRAND.email}`,
      testId: CONTACT.emailLink,
      bg: '#FF7597',
    },
  ];

  return (
    <div className="bg-[#FFFBFB]">
      <section className="px-6 md:px-12 lg:px-24 pt-16 md:pt-24 pb-12">
        <div className="max-w-7xl mx-auto">
          <p className="text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-[#FF7597]">
            {t('contact.eyebrow')}
          </p>
          <h1 className="font-display mt-3 text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-[#1F1A24]">
            {t('contact.title')}
          </h1>
          <p className="mt-4 text-base md:text-lg text-[#574A66] max-w-xl">{t('contact.subtitle')}</p>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 pb-20">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {channels.map((c) => (
            <a
              key={c.key}
              data-testid={c.testId}
              href={c.href}
              target="_blank"
              rel="noreferrer"
              className="group rounded-3xl bg-white border border-[#FF7597]/10 p-6 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(255,117,151,0.15)] transition-all"
            >
              <span
                className="h-12 w-12 rounded-full inline-flex items-center justify-center text-white"
                style={{ backgroundColor: c.bg }}
              >
                <c.icon className="w-5 h-5" />
              </span>
              <p className="mt-5 text-[10px] font-bold tracking-[0.25em] uppercase text-[#574A66]">
                {t(`contact.${c.key}`)}
              </p>
              <p className="mt-1 font-display text-lg font-bold text-[#1F1A24] group-hover:text-[#FF7597] transition-colors break-words">
                {c.value}
              </p>
            </a>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="max-w-3xl mx-auto rounded-[2rem] bg-white border border-[#FF7597]/15 p-6 md:p-10 shadow-[0_24px_60px_rgba(255,117,151,0.10)]">
          <h2 className="font-display text-3xl font-bold tracking-tight">{t('contact.writeUs')}</h2>
          <form data-testid={CONTACT.form} onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t('contact.yourName')} testId={CONTACT.name} value={form.name} onChange={update('name')} required />
            <Field label={t('contact.yourEmail')} type="email" testId={CONTACT.email} value={form.email} onChange={update('email')} required />
            <div className="sm:col-span-2">
              <Label>{t('contact.subject')}</Label>
              <input
                data-testid={CONTACT.subject}
                value={form.subject}
                onChange={update('subject')}
                required
                className="w-full rounded-2xl border border-[#FF7597]/20 bg-[#FFFBFB] px-4 py-3 text-sm focus:outline-none focus:border-[#FF7597] focus:ring-2 focus:ring-[#FF7597]/20"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>{t('contact.message')}</Label>
              <textarea
                data-testid={CONTACT.message}
                value={form.message}
                onChange={update('message')}
                rows={5}
                required
                className="w-full rounded-2xl border border-[#FF7597]/20 bg-[#FFFBFB] px-4 py-3 text-sm focus:outline-none focus:border-[#FF7597] focus:ring-2 focus:ring-[#FF7597]/20"
              />
            </div>
            <button
              data-testid={CONTACT.submit}
              type="submit"
              disabled={submitting}
              className="sm:col-span-2 inline-flex items-center justify-center px-7 py-4 rounded-full bg-[#FF7597] text-white text-sm font-bold tracking-widest uppercase hover:bg-[#FF5C83] hover:scale-[1.02] transition-all disabled:opacity-60"
            >
              {submitting ? t('contact.submitting') : t('contact.submit')}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

const Label = ({ children }) => (
  <label className="block text-[10px] font-bold tracking-[0.25em] uppercase text-[#574A66] mb-2">{children}</label>
);
const Field = ({ label, testId, value, onChange, type = 'text', required }) => (
  <div>
    <Label>{label}</Label>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full rounded-2xl border border-[#FF7597]/20 bg-[#FFFBFB] px-4 py-3 text-sm focus:outline-none focus:border-[#FF7597] focus:ring-2 focus:ring-[#FF7597]/20"
    />
  </div>
);
